import {
  setAmbienceScene,
  startAmbience,
  stopAmbience,
  type AmbienceScene,
} from './ambience';
// effects.ts imports effectsBus() from this module, so the two reference each
// other. Safe here because neither calls into the other while the modules are
// evaluating: the functions only run once a player clicks something.
import { hasEffect, playEffect } from './effects';
import { SOUNDS, type SoundChannel, type SoundName } from './sounds';

/**
 * The audio engine.
 *
 * One AudioContext, one gain node per channel, and a `play(name)` call that is
 * safe from anywhere. Everything degrades quietly: a browser without Web Audio,
 * a context that will not start, or an unknown sound name all end in silence
 * rather than an exception, because no UI interaction should ever fail because
 * of a sound.
 *
 * Browsers refuse to start audio until the player interacts with the page, so
 * the context is created lazily on the first gesture and any sound triggered
 * before that is dropped rather than queued. A burst of noise the moment focus
 * returns is worse than a missed click.
 */

interface Channels {
  master: GainNode;
  ui: GainNode;
  game: GainNode;
  ambience: GainNode;
  music: GainNode;
  /** Spoken acknowledgements. Its own channel so it can be turned down alone. */
  voice: GainNode;
}

let ctx: AudioContext | null = null;
let channels: Channels | null = null;
let unlocked = false;

// Music sits under everything by default: it is a room tone, not a soundtrack.
// These are only the values in force for the instant before the settings store
// pushes the saved ones over them, but they should still agree with it.
const volumes = { master: 0.7, ui: 1, game: 1, ambience: 0.05, voice: 0.85, music: 0.3 };
let muted = false;
let ambienceWanted = false;
let scene: AmbienceScene = 'menu';

const lastPlayed = new Map<SoundName, number>();

function createContext(): void {
  if (ctx) return;
  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return;

  try {
    ctx = new Ctor();
  } catch {
    return;
  }

  const master = ctx.createGain();
  const ui = ctx.createGain();
  const game = ctx.createGain();
  const ambience = ctx.createGain();
  const voice = ctx.createGain();
  const music = ctx.createGain();
  ui.connect(master);
  game.connect(master);
  ambience.connect(master);
  voice.connect(master);
  music.connect(master);
  master.connect(ctx.destination);
  channels = { master, ui, game, ambience, voice, music };
  applyVolumes();
}

function applyVolumes(): void {
  if (!channels || !ctx) return;
  const now = ctx.currentTime;
  channels.master.gain.setTargetAtTime(muted ? 0 : volumes.master, now, 0.02);
  channels.ui.gain.setTargetAtTime(volumes.ui, now, 0.02);
  channels.game.gain.setTargetAtTime(volumes.game, now, 0.02);
  channels.voice.gain.setTargetAtTime(volumes.voice, now, 0.02);
  // Ambience fades rather than snaps, so a slider drag does not sound like a
  // switch being flipped.
  channels.ambience.gain.setTargetAtTime(volumes.ambience * 0.5, now, 0.35);
  // Music fades on the same long constant, and sits low: the slider's full
  // travel tops out well under the effects, because it plays under them.
  channels.music.gain.setTargetAtTime(volumes.music * 0.5, now, 0.35);
}

/** Called from the first real user gesture. Safe to call repeatedly. */
export function unlockAudio(): void {
  createContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    void ctx.resume().then(syncAmbience);
  }
  unlocked = true;
  syncAmbience();
}

export function setVolume(
  channel: 'master' | 'ambience' | 'voice' | 'music' | SoundChannel,
  value: number,
): void {
  volumes[channel] = Math.max(0, Math.min(1, value));
  applyVolumes();
}

export function setMuted(value: boolean): void {
  muted = value;
  applyVolumes();
}

// ---------------------------------------------------------------------------
// Ambience
// ---------------------------------------------------------------------------

/** Ambience only runs once audio is unlocked and someone has asked for it. */
function syncAmbience(): void {
  if (!ctx || !channels) return;
  const shouldRun = ambienceWanted && unlocked && ctx.state === 'running';
  if (shouldRun) {
    startAmbience(ctx, channels.ambience);
    setAmbienceScene(ctx, scene);
  } else {
    stopAmbience();
  }
}

export function setAmbienceEnabled(enabled: boolean): void {
  ambienceWanted = enabled;
  createContext();
  syncAmbience();
}

export function setScene(next: AmbienceScene): void {
  scene = next;
  if (ctx) setAmbienceScene(ctx, scene);
}

/**
 * Play a sound by name, preferring a real recording over the synth.
 *
 * Every sound in this game is synthesised unless a recording earns its bytes,
 * and the ones that do are dropped into `assets/audio/effects` under the name
 * of the sound they replace. Nothing else has to change: the call sites still
 * ask for `click` or `error`, and whether that is an oscillator or a wav is a
 * detail of what happens to be in the folder.
 *
 * The synth stays as the fallback rather than being deleted, so a missing or
 * broken file is a slightly worse noise instead of silence.
 */
export function play(name: SoundName, gainScale = 1): void {
  if (muted || !unlocked || !ctx || !channels) return;
  if (ctx.state !== 'running') return;

  if (hasEffect(name)) {
    void playEffect(name);
    return;
  }

  const spec = SOUNDS[name];
  if (!spec) return;

  if (spec.throttleMs) {
    const now = performance.now();
    const previous = lastPlayed.get(name) ?? -Infinity;
    if (now - previous < spec.throttleMs) return;
    lastPlayed.set(name, now);
  }

  try {
    const bus = ctx.createGain();
    bus.gain.value = spec.gain * gainScale;
    bus.connect(channels[spec.channel]);
    // A few milliseconds of lead time avoids clicks from scheduling at exactly
    // currentTime, which the context may already have passed.
    spec.render({ ctx, out: bus, at: ctx.currentTime + 0.005 });
  } catch {
    // A failed sound must never take an interaction down with it.
  }
}

/**
 * The graph the voice player writes into.
 *
 * Exposed rather than wrapped because voice lines are decoded audio files
 * while everything else here is synthesised, so they need the context and a
 * destination rather than a `render` callback.
 */
/** The graph the streamed score plays into. */
export function musicBus(): { ctx: AudioContext; out: GainNode } | null {
  if (muted || !unlocked || !ctx || !channels) return null;
  return { ctx, out: channels.music };
}

export function voiceBus(): { ctx: AudioContext; out: GainNode } | null {
  if (muted || !unlocked || !ctx || !channels) return null;
  if (ctx.state !== 'running') return null;
  return { ctx, out: channels.voice };
}

/**
 * The graph decoded sound-effect files write into: same contract as the voice
 * bus, but through the game channel, so the effects slider governs them.
 */
export function effectsBus(): { ctx: AudioContext; out: GainNode } | null {
  if (muted || !unlocked || !ctx || !channels) return null;
  if (ctx.state !== 'running') return null;
  return { ctx, out: channels.game };
}

/** Preview a channel while the player drags its slider. */
export function previewChannel(channel: SoundChannel): void {
  play(channel === 'ui' ? 'click' : 'buildComplete');
}

export type { SoundName, SoundChannel };
export type { AmbienceScene };
